import { Button } from '@/components/ui/button';
/**
 * RouteError — error / 404 / 403 screens used by the root router.
 *
 * Three exports:
 *   - <NotFoundScreen />   — 404. Shown by `notFoundComponent` on the
 *                            root route. Renders inside the shell if
 *                            Clerk has an authed session, outside
 *                            otherwise.
 *   - <ForbiddenScreen />  — 403. Dispatched manually by pages that
 *                            detect a role/permission miss (rare —
 *                            most 403s come through the toast).
 *   - <AppCrashScreen />   — 500. Bound to `errorComponent` on the
 *                            root route. Catches render errors inside
 *                            the router tree (complements the root
 *                            <ErrorBoundary> which catches uncaught
 *                            errors outside the router — providers,
 *                            Clerk bootstrap, etc.).
 *
 * All three return to the dashboard via router `Link` so navigation
 * stays inside the SPA and the Clerk session isn't bounced.
 *
 * Spec: 07-polish.md §"404 / 403 / 500 pages".
 */
import { FetchError } from '@/lib/api';
import {
  isRouterInternalError,
  isStaleChunkError,
  reloadForStaleChunk,
  staleChunkReason,
} from '@/lib/stale-chunk';
import { useAuth } from '@clerk/react';
import {
  ArrowClockwise,
  ArrowLeft,
  Copy,
  Ghost,
  Lock,
  WarningOctagon,
} from '@phosphor-icons/react';
import { Link } from '@tanstack/react-router';
import { type ReactElement, type ReactNode, useEffect, useState } from 'react';

// -----------------------------------------------------------------
// Shared frame
// -----------------------------------------------------------------

function RouteErrorFrame({
  icon,
  title,
  description,
  children,
}: {
  icon: ReactElement;
  title: string;
  description?: ReactNode;
  children?: ReactNode;
}): ReactElement {
  return (
    <div
      role="alert"
      className="flex min-h-dvh w-full items-center justify-center bg-[var(--color-paper-alt)] p-6"
    >
      <div className="flex w-full max-w-md flex-col items-center gap-4 rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)] p-8 text-center shadow-[var(--shadow-panel)]">
        {icon}
        <div className="space-y-1">
          <h1 className="text-lg font-semibold text-[var(--color-ink)]">{title}</h1>
          {description && <div className="text-sm text-[var(--color-mute)]">{description}</div>}
        </div>
        {children}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------
// 404 — Not found
// -----------------------------------------------------------------

export function NotFoundScreen(): ReactElement {
  const { isSignedIn } = useAuth();
  return (
    <RouteErrorFrame
      icon={
        <Ghost
          aria-hidden="true"
          size={48}
          weight="light"
          className="text-[var(--color-mute)]/80"
        />
      }
      title="Page not found"
      description={
        <p>
          That URL doesn't exist in this dashboard.
          {isSignedIn
            ? ' The sidebar or command palette can help you find what you need.'
            : ' Sign in to continue.'}
        </p>
      }
    >
      <div className="flex flex-wrap items-center justify-center gap-2">
        {isSignedIn ? (
          <Button render={<Link to="/" />}>
            <ArrowLeft aria-hidden="true" />
            Back to dashboard
          </Button>
        ) : (
          <Button render={<Link to="/sign-in" search={{}} />}>Sign in</Button>
        )}
      </div>
    </RouteErrorFrame>
  );
}

// -----------------------------------------------------------------
// 403 — Forbidden
// -----------------------------------------------------------------

export function ForbiddenScreen({ message }: { message?: string }): ReactElement {
  return (
    <RouteErrorFrame
      icon={
        <Lock aria-hidden="true" size={48} weight="light" className="text-[var(--color-warning)]" />
      }
      title="You don't have access"
      description={<p>{message ?? 'Your role in this workspace does not permit that resource.'}</p>}
    >
      <div className="flex flex-wrap items-center justify-center gap-2">
        <Button render={<Link to="/" />}>
          <ArrowLeft aria-hidden="true" />
          Back to dashboard
        </Button>
      </div>
    </RouteErrorFrame>
  );
}

// -----------------------------------------------------------------
// 500 — Uncaught route render error
// -----------------------------------------------------------------

export interface AppCrashScreenProps {
  error?: Error | unknown;
  reset?: () => void;
}

export function AppCrashScreen({ error, reset }: AppCrashScreenProps): ReactElement {
  const [copied, setCopied] = useState(false);
  const message =
    error instanceof Error ? error.message || error.name : 'The dashboard hit an unexpected error.';
  const requestId = error instanceof FetchError && error.request_id ? error.request_id : undefined;

  // A route lazy-load that fails after a deploy means this tab is
  // holding stale chunk hashes — reload once (guarded) to pick up the
  // new asset graph rather than parking the user on the crash screen.
  // Also covers the router-internal "undefined route option" shape
  // (chunks loaded but mid-cutover state in TanStack Router's preload)
  // — same fix, same once-per-10s guard.
  useEffect(() => {
    if (isStaleChunkError(error) || isRouterInternalError(error)) {
      reloadForStaleChunk(staleChunkReason(error));
    }
  }, [error]);

  async function copyDetails() {
    const payload = {
      error:
        error instanceof Error
          ? { name: error.name, message: error.message, stack: error.stack }
          : String(error),
      requestId,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      url: typeof window !== 'undefined' ? window.location.href : 'unknown',
      time: new Date().toISOString(),
    };
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  }

  return (
    <RouteErrorFrame
      icon={
        <WarningOctagon
          aria-hidden="true"
          size={48}
          weight="light"
          className="text-[var(--color-destructive)]"
        />
      }
      title="Something went wrong"
      description={
        <>
          <p>This page crashed. Try again, or copy the details and share with support.</p>
          <pre className="mt-3 max-h-24 w-full overflow-auto rounded-md border border-[var(--color-line-soft)] bg-[var(--color-paper-alt)] p-2 text-left font-mono text-[11px] leading-snug text-[var(--color-ink)]">
            {message}
          </pre>
          {requestId && (
            <p className="pt-1 font-mono text-xs text-[var(--color-mute)]">
              Request id: {requestId}
            </p>
          )}
        </>
      }
    >
      <div className="flex flex-wrap items-center justify-center gap-2">
        {reset ? (
          <Button onClick={reset}>
            <ArrowClockwise aria-hidden="true" />
            Try again
          </Button>
        ) : (
          <Button
            onClick={() => {
              if (typeof window !== 'undefined') window.location.reload();
            }}
          >
            <ArrowClockwise aria-hidden="true" />
            Try again
          </Button>
        )}
        <Button
          variant="outline"
          onClick={() => {
            void copyDetails();
          }}
        >
          <Copy aria-hidden="true" />
          {copied ? 'Copied' : 'Copy details'}
        </Button>
        <Button variant="ghost" render={<Link to="/" />}>
          <ArrowLeft aria-hidden="true" />
          Back to dashboard
        </Button>
      </div>
    </RouteErrorFrame>
  );
}
